import React, { useEffect, useState } from "react";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from "@mui/material";

import { FrmColorTelaio } from "./frm_color_telaio";
import { useChiusure } from "../../provider/chiusure_provider";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useChiusureDialogs } from "../../provider/dialog_chiusure_provider";
import { Aperture, ChiusuraType, Verso, VersoImpacchettamento, VetroType } from "../../../../../models/pergola_configuration";

interface Accessorio {
  sku: string;
  notes: string;
}

// La categoria mostrata nella tendina non e' il codice della chiusura: va ricavata
// dal campo `tipo` del catalogo, che usa maiuscole diverse a seconda della voce.
const categoriaDaTipo = (tipo: string | undefined) => {
  switch ((tipo ?? "").toLowerCase()) {
    case "catenaccioli":
      return "catenaccioli";
    case "serrature":
      return "serrature";
    default:
      return "nessuna";
  }
};

interface Props {
  tipoChiusura: ChiusuraType;
  tipoVetro: VetroType;
  coloreTelaioNsRal: string;
  coloreTelaioRal: string;
  sistema_chiusura: string | undefined;
  accessori: Accessorio[];
  lunghezzaVetrata: number;
  apertura: Aperture;
  vie: number;
  verso: Verso;
  verso_impacchettamento?: VersoImpacchettamento;
  handleChange: (field: string, value: any) => void;
}

export const FrmChiusuraAndromeda: React.FC<Props> = ({
  tipoChiusura,
  tipoVetro,
  coloreTelaioNsRal,
  coloreTelaioRal,
  accessori,
  apertura,
  vie,
  verso,
  verso_impacchettamento,
  lunghezzaVetrata,
  sistema_chiusura,
  handleChange
}) => {
  const dialogs = useChiusureDialogs();
  const chiusureProvider = useChiusure();
  const codice = sistema_chiusura;
  const [imgElement, setImgElement] = useState<JSX.Element | null>(null);
  const chiusura = chiusureProvider?.list.find(x => x.code === sistema_chiusura);
  const [sistemaChiusura, setSistemaChiusura] = useState<string>(categoriaDaTipo(chiusura?.tipo));
  const calculatedVie = Math.round((lunghezzaVetrata / 1000) / 0.7);
  useEffect(() => {
    handleChange("vie", calculatedVie);
  }, [lunghezzaVetrata]);
  // Il catalogo viene popolato dopo il primo render: riallinea la categoria appena
  // la chiusura salvata diventa risolvibile.
  useEffect(() => {
    if (chiusura) {
      setSistemaChiusura(categoriaDaTipo(chiusura.tipo));
    }
  }, [chiusura?.code]);
  useEffect(() => {
    if (chiusureProvider?.list.length && sistema_chiusura && !chiusura) {
      handleChange("sistema_chiusura", undefined);
      setSistemaChiusura("nessuna");
    }
  }, [chiusureProvider?.list.length, sistema_chiusura]);
  useEffect(() => {
    if (codice && chiusureProvider) {
      chiusureProvider.getChiusuraImgPreview(codice).then(img => {
        if (img) {
          setImgElement(<img src={img.src} alt="" style={{ width: 32, height: 32 }} />);
        } else {
          setImgElement(null);
        }
      });
    } else {
      setImgElement(null);
    }
  }, [codice, chiusureProvider]);

  return (
    <>
      <FormControl fullWidth className="mt-3">
        <InputLabel id="tipo_vetro_label">Tipo di vetro</InputLabel>
        <Select
          labelId="tipo_vetro_label"
          id="tipo_vetro"
          name="tipo_vetro"
          value={tipoVetro}
          label="Tipo di vetro"
          onChange={(e) => handleChange("tipo_vetro", e.target.value)}
        >
          <MenuItem value={VetroType.vetro_temperato_10mm}>Vetro temperato 10mm</MenuItem>
          <MenuItem value={VetroType.vetro_camera_25mm_6_16_6_be}>Vetro Camera 25mm (6+16+6 BE)</MenuItem>
        </Select>
      </FormControl>

      <FrmColorTelaio
        coloreTelaioNsRal={coloreTelaioNsRal}
        coloreTelaioRal={coloreTelaioRal}
        setFieldValue={(field, value) => handleChange(field, value)}
      />
      {tipoChiusura === ChiusuraType.vetrate_ad_impacchettamento_andromeda && (
        <>
          <h6 className="text-primary mt-2">Apertura</h6>
          <FormControl fullWidth className="mt-3">
            <InputLabel id="verso_impacchettamento_label">Direzione Impacchettamento</InputLabel>
            <Select
              labelId="verso_impacchettamento_label"
              id="verso_impacchettamento"
              name="verso_impacchettamento"
              value={verso_impacchettamento || VersoImpacchettamento.interna}
              label="Direzione Impacchettamento"
              onChange={(e) => handleChange("verso_impacchettamento", e.target.value)}
            >
              <MenuItem value={VersoImpacchettamento.interna}>Interna</MenuItem>
              <MenuItem value={VersoImpacchettamento.esterna}>Esterna</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth className="mt-3">
            <InputLabel id="verso_apertura_label">Verso Apertura</InputLabel>
            <Select
              labelId="verso_apertura_label"
              id="verso_apertura"
              name="apertura"
              value={apertura || Aperture.laterale}
              label="Verso Apertura"
              onChange={(e) => handleChange("apertura", e.target.value)}
            >
              <MenuItem value={Aperture.laterale}>Laterale</MenuItem>
              <MenuItem value={Aperture.centrale}>Centrale</MenuItem>
            </Select>
          </FormControl>
          {apertura === Aperture.laterale && (
            <FormControl fullWidth className="mt-3">
              <InputLabel id="apertura_label">Direzione Apertura (Vista Interna)</InputLabel>
              <Select
                labelId="apertura_label"
                id="direzione_apertura"
                name="verso"
                value={verso || Verso.destra}
                label="Direzione Apertura (Vista Interna)"
                onChange={(e) => handleChange("verso", e.target.value)}
              >
                <MenuItem value={Verso.destra}>Destra</MenuItem>
                <MenuItem value={Verso.sinistra}>Sinistra</MenuItem>
              </Select>
            </FormControl>
          )}
          <h6 className="text-primary mt-3">Sistema di chiusura</h6>
          {tipoVetro === VetroType.vetro_temperato_10mm && (
            <FormControl fullWidth className="mt-1">
              <InputLabel id="sistema_chiusura_label">Sistema di chiusura</InputLabel>
              <Select
                labelId="sistema_chiusura_label"
                value={sistemaChiusura}
                onChange={(e) => {
                  setSistemaChiusura(e.target.value);
                  // Cambiando categoria il codice gia' scelto non e' piu' valido.
                  if (sistema_chiusura) handleChange("sistema_chiusura", undefined);
                }}
                label="Sistema di chiusura"
              >
                <MenuItem value="nessuna">NESSUNA</MenuItem>   
                <MenuItem value="catenaccioli">CATENACCIOLI</MenuItem>
                <MenuItem value="serrature">SERRATURE</MenuItem>
              </Select>

              {sistemaChiusura == "catenaccioli" && (
                <TextField
                  fullWidth
                  className="mt-2"
                  label={!codice ? "Seleziona chiusura" : ""}
                  name="chiusuraCodice"
                  value={chiusureProvider?.getChiusuraName(codice) || ""}
                  InputProps={{
                    readOnly: true,
                    startAdornment: codice ? <InputAdornment position="start">{imgElement}</InputAdornment> : null,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => {
                            if (!chiusureProvider?.list.length) {
                              alert("Le chiusure non sono ancora disponibili, riprova più tardi.");
                              return;
                            }
                            dialogs?.selectChiusuraCatenaccioli({
                              chiusure: chiusureProvider?.list || [],
                              selectedChiusura: codice || null,
                              onConfirm: (code) => {
                                if (!code) return;
                                handleChange("sistema_chiusura", code);
                              }
                            });
                          }}
                        >
                          <OpenInNewIcon style={{ color: "black" }} />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              )}

              {sistemaChiusura == "serrature" && (
                <TextField
                  fullWidth
                  className="mt-2"
                  label={!codice ? "Seleziona chiusura" : ""}
                  name="chiusuraCodice"
                  value={chiusureProvider?.getChiusuraName(codice) || ""}
                  InputProps={{
                    readOnly: true,
                    startAdornment: codice ? <InputAdornment position="start">{imgElement}</InputAdornment> : null,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => {
                            if (!chiusureProvider?.list.length) {
                              alert("Le chiusure non sono ancora disponibili, riprova più tardi.");
                              return;
                            }
                            dialogs?.selectChiusuraSerrature({
                              chiusure: chiusureProvider?.list || [],
                              selectedChiusura: codice || null,
                              onConfirm: (code) => {
                                if (!code) return;
                                handleChange("sistema_chiusura", code);
                              }
                            });
                          }}
                        >
                          <OpenInNewIcon style={{ color: "black" }} />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              )}
            </FormControl>
          )}
          <TextField
            fullWidth
            className="mt-3"
            label="Numero di vie"
            name="vie"
            id="vie"
            value={vie}                     // ← prendi il valore dal prop
            InputProps={{
              readOnly: true,
            }}
            hidden={true}
            variant="outlined"
          />
          {tipoVetro === VetroType.vetro_camera_25mm_6_16_6_be && (
            <TextField
              fullWidth
              className="mt-2"
              label={!codice ? "Seleziona chiusura" : ""}
              name="chiusuraCodice"
              value={chiusureProvider?.getChiusuraName(codice) || ""}
              InputProps={{
                readOnly: true,
                startAdornment: codice ? <InputAdornment position="start">{imgElement}</InputAdornment> : null,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => {
                        if (!chiusureProvider?.list.length) {
                          alert("Le chiusure non sono ancora disponibili, riprova più tardi.");
                          return;
                        }
                        dialogs?.selectChiusuraVetro({
                          chiusure: chiusureProvider?.list || [],
                          selectedChiusura: codice || null,
                          onConfirm: (code) => {
                            if (!code) return;
                            handleChange("sistema_chiusura", code);
                          }
                        });
                      }}
                    >
                      <OpenInNewIcon style={{ color: "black" }} />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          )}

          <h6 className="text-primary mt-2">Optional</h6>
          {[
            { sku: "andromeda_extra_opening", name: "Apertura Aggiuntiva" },
            { sku: "ACCT0175", name: "Profilo inferiore per incasso a pavimento (al metro lineare)" }
          ].map(opt => (
            <FormControlLabel
              key={opt.sku}
              className="w-full mt-2"
              control={
                <Checkbox
                  checked={accessori.some(a => a.sku === opt.sku)}
                  onChange={(e) => {
                    let updated = [...accessori];
                    if (e.target.checked) {
                      if (!updated.find(a => a.sku === opt.sku)) {
                        updated.push({ sku: opt.sku, notes: "" });
                      }
                    } else {
                      updated = updated.filter(a => a.sku !== opt.sku);
                    }
                    handleChange("accessori", updated);
                  }}
                  sx={{ marginTop: 0, marginBottom: 0 }}
                />
              }
              label={opt.name}
              sx={{ marginTop: 0, marginBottom: 0 }}
            />
          ))}
        </>
      )}
    </>
  );
};
